<template>
  <v-dialog
    :model-value="open"
    max-width="760"
    scrim="rgba(0, 0, 0, 0.85)"
    @click:outside="emitClose"
  >
    <article v-if="project" class="dialog dark-card">
      <header class="dialog__header">
        <div>
          <span class="dialog__category">{{ project.category }}</span>
          <h2 class="heading-md">{{ project.title }}</h2>
        </div>
        <button type="button" class="dialog__close" aria-label="Close" @click="emitClose">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </header>

      <div class="dialog__visual">
        <span class="dialog__initial">{{ project.title.charAt(0) }}</span>
      </div>

      <div class="dialog__body">
        <p class="body-sm">{{ project.description }}</p>

        <div v-if="project.features?.length" class="dialog__section">
          <h3 class="dialog__label">Features</h3>
          <ul class="dialog__list">
            <li v-for="(feat, i) in project.features" :key="i">{{ feat }}</li>
          </ul>
        </div>

        <div v-if="project.techStack?.length" class="dialog__section">
          <h3 class="dialog__label">Tech Stack</h3>
          <div class="dialog__tags">
            <span v-for="tech in project.techStack" :key="tech" class="tag">{{ tech }}</span>
          </div>
        </div>

        <div class="dialog__actions">
          <a
            v-if="project.liveDemo"
            :href="project.liveDemo"
            class="btn btn--accent"
            target="_blank"
            rel="noopener noreferrer"
          >
            View Project
            <v-icon size="16">mdi-open-in-new</v-icon>
          </a>
          <a
            v-if="project.sourceCode"
            :href="project.sourceCode"
            class="btn btn--ghost"
            target="_blank"
            rel="noopener noreferrer"
          >
            Source Code
          </a>
        </div>
      </div>
    </article>
  </v-dialog>
</template>

<script setup>
defineProps({
  open: { type: Boolean, default: false },
  project: { type: Object, default: null },
});

const emit = defineEmits(["close"]);
const emitClose = () => emit("close");
</script>

<style scoped>
.dialog {
  overflow: hidden;
  border-top: 2px solid var(--color-accent);
}

.dialog__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem 1.5rem 0;
  gap: 1rem;
}

.dialog__category {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-accent);
  margin-bottom: 0.375rem;
}

.dialog__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  color: var(--color-text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: all var(--transition);
}

.dialog__close:hover {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: var(--color-text);
}

.dialog__visual {
  margin: 1.25rem 1.5rem 0;
  aspect-ratio: 16 / 7;
  background: var(--color-surface-2);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog__initial {
  font-size: 3.5rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.06);
}

.dialog__body {
  padding: 1.5rem;
}

.dialog__section {
  margin-top: 1.5rem;
}

.dialog__label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-text-faint);
  margin: 0 0 0.75rem;
}

.dialog__list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.dialog__list li {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  padding: 0.3rem 0;
}

.dialog__tags {
  display: flex;
  flex-wrap: wrap;
}

.dialog__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.5rem;
}
</style>
